import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ADMIN_DELETES_FLAG_KEY, useFeatureFlagCheck } from "@/hooks/use-feature-flag";
import { IconLoader2 } from "@tabler/icons-react";
import { useState } from "react";

interface ConfirmDestructiveDialogProps {
  /**
   * What the user must type verbatim to enable the destructive action — the
   * entity's primary ID. Omit to skip the typed-confirmation step entirely.
   */
  confirmValue?: string;
  /** Label for what's being typed, e.g. "club ID", "badge ID". Required with `confirmValue`. */
  confirmLabel?: string;
  title: React.ReactNode;
  description: React.ReactNode;
  /** Text on the destructive confirm button. Defaults to "Delete". */
  actionLabel?: string;
  /** Called only once the typed value matches and the flag is enabled. */
  onConfirm: () => void;
  /** Whether the destructive mutation is in flight. */
  isPending: boolean;
  /** Render prop for the trigger element; receives whether it should be disabled and why. */
  trigger: (state: { disabled: boolean; disabledReason: string | null }) => React.ReactNode;
  /** Feature flag key to gate on. Defaults to "enable_admin_deletes". */
  featureFlagKey?: string;
  /** Skip the feature flag gate entirely — the action is only confirmed by typing the value. */
  skipFeatureFlag?: boolean;
  /** Extra content rendered between the description and the confirm input (e.g. an image preview). */
  children?: React.ReactNode;
}

export function ConfirmDestructiveDialog({
  confirmValue,
  confirmLabel,
  title,
  description,
  actionLabel = "Delete",
  onConfirm,
  isPending,
  trigger,
  featureFlagKey = ADMIN_DELETES_FLAG_KEY,
  skipFeatureFlag = false,
  children,
}: ConfirmDestructiveDialogProps) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlagCheck(featureFlagKey);

  const flagBlocked = !skipFeatureFlag && (flagLoading || !flagEnabled);
  const disabledReason = skipFeatureFlag
    ? null
    : flagLoading
      ? "Checking permissions…"
      : !flagEnabled
        ? "Deletion is disabled by feature flag."
        : null;

  function handleOpenChange(next: boolean) {
    if (isPending) return;
    setOpen(next);
    if (!next) setTyped("");
  }

  const requiresTypedConfirm = confirmValue !== undefined;
  const canConfirm =
    (!requiresTypedConfirm || typed === confirmValue) &&
    !flagBlocked &&
    !isPending;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" tabIndex={flagBlocked ? 0 : -1}>
            <AlertDialogTrigger asChild disabled={flagBlocked}>
              {trigger({ disabled: flagBlocked, disabledReason })}
            </AlertDialogTrigger>
          </span>
        </TooltipTrigger>
        {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
      </Tooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        {requiresTypedConfirm && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm-destructive-value" className="text-xs">
              Type <span className="font-mono font-semibold break-all">{confirmValue}</span> (
              {confirmLabel}) to confirm
            </Label>
            <Input
              id="confirm-destructive-value"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              disabled={isPending}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {isPending ? <IconLoader2 className="size-3.5 animate-spin" /> : actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
